import { useEffect, useState } from 'react'
import type { DetectionEvent } from './eventsApi'

export function EventExportActions({ event, video, snapshot, loading }: {
  event: DetectionEvent; video?: string; snapshot?: string; loading?: boolean
}) {
  const [kind, setKind] = useState<'video' | 'snapshot'>(event.hasVideo ? 'video' : 'snapshot')
  const [file, setFile] = useState<File | null>(null)
  const [sharing, setSharing] = useState(false)
  const [notice, setNotice] = useState('')
  const [preparing, setPreparing] = useState(false)
  const source = kind === 'video' ? video : snapshot
  const caption = [event.companyName || 'Spectra Guard AI', event.clientName, event.cameraName,
    new Date(event.occurredAtUtc).toLocaleString('pt-BR'), event.analyticNames || event.eventType].filter(Boolean).join(' · ')

  useEffect(() => {
    let active = true
    setFile(null); setNotice(''); setPreparing(Boolean(source))
    if (source) void fetch(source).then(response => response.blob()).then(blob => {
      if (!active) return
      const extension = kind === 'video' ? 'mp4' : blob.type === 'image/png' ? 'png' : 'jpg'
      const type = kind === 'video' ? 'video/mp4' : extension === 'png' ? 'image/png' : 'image/jpeg'
      setFile(new File([blob], `evento-${event.occurredAtUtc.slice(0, 19).replace(/[^0-9T]/g, '-')}-${event.id.slice(0, 8)}.${extension}`, { type }))
    }).catch(() => { if (active) setNotice('Não foi possível preparar o arquivo. Reabra o evento e tente novamente.') })
      .finally(() => { if (active) setPreparing(false) })
    return () => { active = false }
  }, [source, kind, event.id, event.occurredAtUtc])

  function download() {
    if (!file) return
    const url = URL.createObjectURL(file)
    const link = document.createElement('a')
    link.href = url; link.download = file.name
    document.body.appendChild(link); link.click(); link.remove()
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }

  async function share() {
    if (!file || sharing) return
    setNotice('')
    if (!navigator.share || !navigator.canShare?.({ files: [file] })) {
      download()
      const mobile = /Android|iPhone|iPad/i.test(navigator.userAgent)
      window.open(mobile ? `https://wa.me/?text=${encodeURIComponent(caption)}` : 'https://web.whatsapp.com/', '_blank', 'noopener,noreferrer')
      setNotice('Download iniciado. No WhatsApp, escolha a conversa e anexe o arquivo de Downloads.')
      return
    }
    setSharing(true)
    try {
      // The file is prepared before the click so Web Share keeps user activation.
      await navigator.share({ files: [file], title: 'Evento de segurança', text: caption })
    } catch (failure) {
      if (!(failure instanceof DOMException && failure.name === 'AbortError')) {
        setNotice('Não foi possível abrir o compartilhamento. Use Baixar e anexe o arquivo no WhatsApp.')
      }
    } finally { setSharing(false) }
  }

  if (!event.hasVideo && !event.hasSnapshot) return null
  const busy = loading || preparing
  return <section className="event-export" aria-label="Exportar evento">
    <strong>Exportar evento</strong>
    <div className="event-export-actions">
      <label>Arquivo<select aria-label="Arquivo do evento" value={kind} disabled={sharing} onChange={e => setKind(e.target.value as 'video' | 'snapshot')}>
        {event.hasVideo && <option value="video">Vídeo</option>}
        {event.hasSnapshot && <option value="snapshot">Imagem</option>}
      </select></label>
      <button type="button" disabled={!file || sharing || busy} onClick={() => { download(); setNotice('Download iniciado. O arquivo está disponível nos downloads do navegador.') }}>↓ Baixar</button>
      <button type="button" disabled={!file || sharing || busy} onClick={() => void share()}>↗ {sharing ? 'Abrindo…' : 'WhatsApp'}</button>
    </div>
    <small>Compartilhe o arquivo do evento. No celular, selecione WhatsApp na lista de aplicativos.</small>
    <p role="status" aria-live="polite">{busy ? 'Preparando arquivo…' : notice || (!file ? 'Arquivo indisponível. Reabra o evento para tentar novamente.' : '')}</p>
  </section>
}
